// frontend/src/components/PublicationCounter.jsx
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../config/api';
import { TrendingUp, AlertCircle, CheckCircle, Info } from 'lucide-react';

export const PublicationCounter = () => {
    const { user } = useAuth();
    const [stats, setStats] = useState({
        total: 0,
        remaining: 10,
        limit: 10,
        percentage: 0,
        is_near_limit: false,
        is_exceeded: false
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (user) {
            fetchStats();
        }
    }, [user]);

    const fetchStats = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.get('/user/publication-stats');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching publication stats:', error);
            setError('Não foi possível carregar as estatísticas');
        } finally {
            setLoading(false);
        }
    };

    // Si no hay usuario o está cargando
    if (!user) return null;

    // Si no tiene unidad
    if (error && error.includes('unidade')) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Info className="w-5 h-5 text-yellow-600 mx-auto mb-2" />
                <p className="text-sm text-yellow-700">
                    Complete seu cadastro com endereço para publicar anúncios.
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 animate-pulse">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                        <div className="h-2 bg-gray-200 rounded w-full"></div>
                    </div>
                </div>
            </div>
        );
    }

    const { total, remaining, limit, percentage, is_near_limit, is_exceeded } = stats;

    // Determinar estado visual
    let statusColor = 'text-[#449879]';
    let bgColor = 'bg-[#449879]';
    let borderColor = 'border-[#449879]';
    let message = `${remaining} publicação${remaining > 1 ? 'ões' : ''} disponível${remaining > 1 ? 'is' : ''}`;
    let icon = <CheckCircle className="w-5 h-5 text-[#449879]" />;

    if (is_exceeded) {
        statusColor = 'text-red-600';
        bgColor = 'bg-red-500';
        borderColor = 'border-red-500';
        message = '🚫 Limite excedido!';
        icon = <AlertCircle className="w-5 h-5 text-red-500" />;
    } else if (is_near_limit) {
        statusColor = 'text-yellow-600';
        bgColor = 'bg-yellow-500';
        borderColor = 'border-yellow-500';
        message = `⚠️ Restam ${remaining} publicação${remaining > 1 ? 'ões' : ''}`;
        icon = <AlertCircle className="w-5 h-5 text-yellow-500" />;
    }

    return (
        <div className={`bg-white rounded-lg shadow-sm border ${borderColor} p-4 transition-all duration-300`}>
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${is_exceeded ? 'bg-red-50' : is_near_limit ? 'bg-yellow-50' : 'bg-green-50'}`}>
                        <TrendingUp className={`w-5 h-5 ${is_exceeded ? 'text-red-500' : is_near_limit ? 'text-yellow-500' : 'text-[#449879]'}`} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-700">Publicações do mês</p>
                        <p className={`text-xs font-semibold ${statusColor}`}>
                            {total} de {limit} utilizadas
                        </p>
                    </div>
                </div>
                <div className="text-right">
                    <span className={`text-sm font-bold ${statusColor}`}>
                        {remaining}
                    </span>
                    <p className="text-xs text-gray-400">restantes</p>
                </div>
            </div>

            {/* Barra de progreso */}
            <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                        className={`h-2.5 rounded-full transition-all duration-500 ${bgColor}`}
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                    ></div>
                </div>
                <p className={`text-xs mt-1 flex items-center gap-1 ${is_exceeded ? 'text-red-600' : is_near_limit ? 'text-yellow-600' : 'text-gray-500'}`}>
                    {icon}
                    <span>{message}</span>
                </p>
            </div>

            {/* Alerta crítica - solo si es la última publicación */}
            {is_near_limit && remaining === 1 && (
                <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                    <p className="text-xs text-red-700 font-medium">
                        🚨 ÚLTIMA PUBLICAÇÃO DISPONÍVEL NESTE MÊS!
                    </p>
                </div>
            )}

            {/* Alerta de excedido */}
            {is_exceeded && (
                <div className="mt-2 bg-red-100 border border-red-300 rounded-lg p-2 text-center">
                    <p className="text-xs text-red-700 font-medium">
                        ⛔ Limite de 10 publicações atingido. Aguarde o próximo mês.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PublicationCounter;